import { useState, useEffect, useCallback, useMemo } from "react";

import { BaseApiService } from "../../services/base/base-api.service";
import { DTOModel } from "../../models/base/dto-model";
import { STOModel } from "../../models/base/sto-model";

import { Params, AbortableLoadResponse } from "./interfaces";

function useAbortableLoadResponse<T>(
  apiService: any, // apiService: uninitialized API Service
  params: Params | any, // params: params object ({propsFilter?: PropsFilter, searchFilter?: string, sorts?: Sort[], page?: Page, exactMatch?: boolean, timeout?: number})
  loadItemsPath?: string // loadItemsPath?: alternative path for get items
): AbortableLoadResponse<T> {
  const [state, setState] = useState<AbortableLoadResponse<T>>({
    response: null,
    error: "",
    loading: false,
  });

  const stringParams = useMemo(() => JSON.stringify(params), [params]);

  const apiServiceInstance: BaseApiService<T extends DTOModel ? T : never, STOModel> = useMemo(() => {
    const serviceApi = new apiService();

    if (loadItemsPath) serviceApi.useLoadItemsPath(loadItemsPath);

    return serviceApi;
  }, [apiService, loadItemsPath]);

  const doFetch = useCallback(
    async (signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      const objParams: Params = JSON.parse(stringParams);

      try {
        const response = await apiServiceInstance.loadItems(
          objParams.propsFilter,
          objParams.searchFilter,
          objParams.sorts,
          objParams.page,
          objParams.exactMatch,
          objParams.timeout,
          signal
        );

        if (!signal.aborted) setState((state) => ({ ...state, error: response.error, response, loading: false }));
      } catch (error) {
        if (!signal.aborted)
          setState((state) => ({
            ...state,
            error: error as string,
            loading: false,
          }));
      }
    },
    [apiServiceInstance, stringParams]
  );

  useEffect(() => {
    const abortController = new AbortController();

    doFetch(abortController.signal);

    return () => abortController.abort();
  }, [doFetch]);

  return {
    response: state.response,
    error: state.error,
    loading: state.loading,
  };
}
export default useAbortableLoadResponse;
